import { motion } from "framer-motion";
import { Pic } from "./Pic";

const Star = ({ className, size = 18, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} style={style} aria-hidden>
    <path d="M12 1.5 C12.8 7.5 16.5 11.2 22.5 12 C16.5 12.8 12.8 16.5 12 22.5 C11.2 16.5 7.5 12.8 1.5 12 C7.5 11.2 11.2 7.5 12 1.5 Z" fill="currentColor" />
  </svg>
);

// Page-specific sparse decoration: soft blobs + a few brand stars in different arrangements.
const DECOR = {
  experiences: { blobs: ["left-[-10%] top-[-20%] bg-en-yellow/20", "right-[30%] bottom-[-30%] bg-en-teal/15", "right-[-8%] top-[10%] bg-[#FF6B6B]/10"], stars: [["left-[6%] top-[12%]", "text-en-yellow", 18], ["left-[40%] top-[6%]", "text-en-teal", 12]] },
  audiences: { blobs: ["right-[-12%] top-[-25%] bg-en-teal/10", "left-[-8%] bottom-[-30%] bg-en-yellow/15"], stars: [["left-[4%] top-[18%]", "text-en-teal", 20], ["right-[8%] top-[8%]", "text-en-yellow", 14]] },
  sponsorships: { blobs: ["left-[20%] top-[-30%] bg-en-yellow/15", "right-[-10%] bottom-[-20%] bg-en-teal/10"], stars: [["left-[3%] top-[30%]", "text-en-yellow", 16]] },
  about: { blobs: ["left-[-10%] top-[-20%] bg-en-teal/10", "right-[-6%] top-[-10%] bg-en-yellow/15"], stars: [["right-[12%] top-[10%]", "text-en-teal", 18], ["left-[48%] top-[6%]", "text-en-yellow", 12]] },
  contact: { blobs: ["right-[-10%] top-[-30%] bg-en-yellow/15", "left-[-10%] bottom-[-30%] bg-en-teal/10"], stars: [["right-[6%] top-[14%]", "text-en-yellow", 18]] },
  plain: { blobs: ["right-[-10%] top-[-30%] bg-en-teal/10"], stars: [] },
};

const Decor = ({ variant }) => {
  const d = DECOR[variant] || DECOR.plain;
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 -z-[1] overflow-hidden">
      {d.blobs.map((c, i) => <div key={i} className={`hero-blob absolute h-[60vw] max-h-[560px] w-[60vw] max-w-[560px] rounded-full blur-3xl ${c}`} style={{ animationDelay: `${-i * 7}s` }} />)}
      {d.stars.map(([pos, color, size], i) => <Star key={i} size={size} className={`hero-shape hidden md:block ${pos} ${color}`} style={{ "--d": `${-i * 5}s` }} />)}
    </div>
  );
};

const Words = ({ text, fx }) =>
  text.split(" ").map((w, i) => (
    <span key={i}><span className={`hero-word hero-word-${fx}`} style={{ "--i": i }}>{w}</span>{" "}</span>
  ));

export const PageHero = ({ eyebrow, eyebrowItems, title, titleFx, intro, image, imageAlt = "", testId, actions, visual, variant = "plain", below, topBand, footer, className = "" }) => {
  const hasSide = Boolean(image || visual);
  return (
    <section className={`relative isolate overflow-hidden bg-white ${className}`} data-testid={testId}>
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10" style={{ background: "linear-gradient(to bottom, #fff 0%, #fff 60%, rgba(20,155,158,0.08) 75%, rgba(20,155,158,0.35) 100%)" }} />
      <Decor variant={variant} />
      {topBand}
      <div className={`mx-auto grid max-w-7xl items-center gap-10 px-5 pt-14 sm:px-8 lg:gap-14 lg:pt-20 ${footer ? "pb-0 lg:pb-0" : "pb-16 lg:pb-20"} ${hasSide ? "lg:grid-cols-12" : ""}`}>
        <div className={hasSide ? "lg:col-span-6" : "max-w-4xl"}>
          {eyebrowItems ? (
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="eyebrow mb-4 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`${testId}-eyebrow`}>
              {eyebrowItems.map((item, i) => <span key={item} className="inline-flex items-center gap-x-3">{i > 0 && <span aria-hidden className="text-[0.8em] leading-none text-en-yellow">★</span>}<span>{item}</span></span>)}
            </motion.p>
          ) : (
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="eyebrow mb-4" data-testid={`${testId}-eyebrow`}>{eyebrow}</motion.p>
          )}
          <h1 data-testid={`${testId}-title`} className={`font-display text-4xl leading-[1.02] text-black sm:text-5xl lg:text-6xl ${titleFx === "underline" ? "hero-underline-wrap" : ""}`}>
            {titleFx && titleFx !== "underline" ? <Words text={title} fx={titleFx} /> : title}
            {titleFx === "underline" && (
              <svg aria-hidden className="hero-underline" viewBox="0 0 300 14" preserveAspectRatio="none"><path d="M4 9 C 60 2, 120 12, 180 7 S 260 4, 296 8" fill="none" stroke="#149B9E" strokeWidth="4" strokeLinecap="round" /></svg>
            )}
          </h1>
          {intro && (
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }} className="mt-6 max-w-2xl text-base leading-relaxed text-black/70 sm:text-lg">{intro}</motion.p>
          )}
          {below}
          {actions && <div className="mt-8 flex flex-wrap gap-4">{actions}</div>}
        </div>
        {visual && <div className="lg:col-span-6">{visual}</div>}
        {image && !visual && (
          <div className="lg:col-span-6">
            <Pic src={image} alt={imageAlt} priority data-testid={`${testId}-photo`} className="aspect-[4/3] w-full rounded-3xl border border-black/10 object-cover shadow-[0_40px_80px_-30px_rgba(0,0,0,0.45)] lg:aspect-[5/4]" />
          </div>
        )}
      </div>
      {footer && <div className="mx-auto max-w-7xl px-5 pb-16 sm:px-8 lg:pb-20">{footer}</div>}
    </section>
  );
};
